import axios from 'axios';
import * as types from '@/store/mutation-types';
import {Form} from '@/plugins/FWForms/index';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import {handleApiError} from '@/utils/errorHandler.ts';
import type {GetterTree, MutationTree} from 'vuex';
import type {RootState} from '@/types/RootState';
import {push} from 'notivue'

export interface PricingRule {
    name: string;
    rule_type: string;
    rules: any[];
    exclusions: any[];
    created_at?: string;
    updated_at?: string;
    uuid: string;
}

export interface PriceTier {
    name: string;
    quantity: number;
    period: 'hours' | 'days' | 'weeks' | 'months' | 'years';
    multiplier: number;
    example_price: number;
}

export interface PriceStructure {
    id?: number;
    uuid: string | null;
    name: string;
    price_example: number;
    added_amount: number;
    duration_unit: 'hours' | 'days' | 'weeks' | 'months' | 'years';
    multiplier: number;
    calculation_mode: 'tiered' | 'repeatable';
    price_tiers: PriceTier[];
}

type LoadingSubject =
    | 'get'
    | 'create'
    | 'update'
    | 'delete'
    | 'toggle'
    | 'structures'
    | 'structure'
    | 'structureUpdate'
    | 'structureDelete'
    | 'structureCreate';

type LoadingState = Record<LoadingSubject, boolean>;

export interface PricingState {
    rulesVersion: number;
    loading: LoadingState;
    currentWorkingPricingStructure: PriceStructure;
    couponForm: Form<any>;
    pricingRules: PricingRule[];
    pricingStructure: PriceStructure | null;
    pricingStructures: PriceStructure[];
}

const DEFAULT_WORKING_STRUCTURE: Readonly<PriceStructure> = {
    uuid: null,
    name: '',
    price_example: 100,
    added_amount: 100,
    duration_unit: 'days',
    multiplier: 1,
    calculation_mode: 'tiered',
    price_tiers: [
        {
            name: '1 day',
            quantity: 1,
            period: 'days',
            multiplier: 1,
            example_price: 100,
        },
    ],
};

const initialLoading = (): LoadingState => ({
    get: false,
    create: false,
    update: false,
    delete: false,
    toggle: false,
    structures: false,
    structure: false,
    structureUpdate: false,
    structureDelete: false,
    structureCreate: false,
});

// state
const state: PricingState = {
    rulesVersion: 0,
    loading: initialLoading(),
    currentWorkingPricingStructure: {...DEFAULT_WORKING_STRUCTURE},
    couponForm: new Form<any>(),
    pricingRules: [],
    pricingStructure: null,
    pricingStructures: [],
};

const getters: GetterTree<PricingState, RootState> = {
    rulesVersion: (state: PricingState) => state.rulesVersion,
    loading: state => state.loading,
    pricingRules: state => state.pricingRules,
    pricingStructure: state => state.pricingStructure,
    pricingStructures: state => state.pricingStructures,
    currentWorkingPricingStructure: state => state.currentWorkingPricingStructure,
};

const mutations: MutationTree<PricingState> = {
    [types.TOUCH_PRICING_RULES](state: PricingState) {
        state.rulesVersion += 1;
    },
    [types.SET_PRICING_LOADING](state, payload: { subject: LoadingSubject; loading: boolean }) {
        state.loading[payload.subject] = payload.loading;
    },
    [types.SET_PRICING_RULES](state, rules: PricingRule[]) {
        state.pricingRules = rules;
    },
    [types.SET_PRICING_STRUCTURES](state, data: PriceStructure[]) {
        state.pricingStructures = data;
    },
    [types.SET_PRICING_STRUCTURE](state, data: PriceStructure) {
        state.pricingStructure = data;
    },
    [types.RESET_CURRENT_WORKING_PRICING_STRUCTURE](state) {
        state.currentWorkingPricingStructure = {...DEFAULT_WORKING_STRUCTURE};
    },
};

const actions = {

    /**
     * Get pricing rules
     */
    async getPricingRules({commit}): Promise<void> {
        commit(types.SET_PRICING_LOADING, {subject: 'get', loading: true} as const);

        return axios.get('/v1/pricing-rules')
            .then((response) => {
                commit('SET_PRICING_RULES', response?.data?.rules);
            }).catch(() => {
            }).finally(() => {
                commit('SET_PRICING_LOADING', {subject: 'get', loading: false});
            });
    },

    /**
     * Create a pricing rule
     */
    async createPricingRule({commit}, rule: Partial<PricingRule>): Promise<void> {
        commit(types.SET_PRICING_LOADING, {subject: 'create', loading: true} as const);

        try {
            await performApiRequest(commit, 'SET_PRICING_LOADING', 'create', () => axios.post('/v1/pricing-rules', rule));
            commit(types.TOUCH_PRICING_RULES);
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Update pricing rule
     */
    async savePricingRule({commit}, ruleData: PricingRule): Promise<void> {
        commit(types.SET_PRICING_LOADING, {subject: 'update', loading: true} as const);

        axios.put(`/v1/pricing-rules/${ruleData.uuid}`, ruleData)
            .then(() => {
            })
            .catch((error) => {
                handleApiError(error);
            })
            .finally(() => {
                commit('SET_PRICING_LOADING', {subject: 'update', loading: false});
            });
    },

    /**
     * Delete pricing rules
     */
    async deletePricingRules({commit}, uuids: string[]): Promise<void> {
        try {
            await performApiRequest(commit, 'SET_PRICING_LOADING', 'delete', () => axios.post('/v1/pricing-rules/delete', {uuids}));
            commit(types.TOUCH_PRICING_RULES);
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Pricing structures
     */
    getPricingStructures({commit}) {
        commit('SET_PRICING_LOADING', {subject: 'structures', loading: true});

        axios.get('/v1/price-structures')
            .then((response) => {
                commit('SET_PRICING_STRUCTURES', response?.data?.data);
            })
            .catch((error) => {
                handleApiError(error);
            })
            .finally(() => {
                commit('SET_PRICING_LOADING', {subject: 'structures', loading: false});
            });
    },

    /**
     * Single pricing structures
     */
    getPricingStructure({commit}, uuid: string) {
        commit('SET_PRICING_LOADING', {subject: 'structure', loading: true});

        axios.get(`/v1/price-structures/${uuid}`)
            .then((response) => {
                commit('SET_PRICING_STRUCTURE', response?.data?.data);
            })
            .catch((error) => {
                handleApiError(error);
            })
            .finally(() => {
                commit('SET_PRICING_LOADING', {subject: 'structure', loading: false});
            });
    },

    /**
     * Save pricing structures
     */
    async savePriceStructure({commit}, payload: Omit<PriceStructure, 'uuid'>): Promise<boolean | void> {
        commit('SET_PRICING_LOADING', {subject: 'structureCreate', loading: true});

        return axios.post('/v1/price-structures', payload)
            .then(() => {
                return true;
            })
            .catch((error) => {
                handleApiError(error);
            })
            .finally(() => {
                commit(types.SET_PRICING_LOADING, {subject: 'structureCreate', loading: false} as const);
            });
    },

    /**
     * Update pricing structures
     */
    async updatePriceStructure({commit}, payload: PriceStructure): Promise<void> {
        try {
            await performApiRequest(commit, 'SET_PRICING_LOADING', 'structureUpdate', () => axios.put(`/v1/price-structures/${payload.uuid}`, payload));
            push.success({
                message: 'Pricing structure updated successfully.',
            });
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Delete pricing structures
     */
    async deletePriceStructure({commit}, payload: { uuid: string }): Promise<boolean | void> {
        commit('SET_PRICING_LOADING', {subject: 'structureDelete', loading: true});

        return axios.delete(`/v1/price-structures/${payload.uuid}`)
            .then(() => {
                return true;
            })
            .catch((error) => {
                handleApiError(error);
            })
            .finally(() => {
                commit('SET_PRICING_LOADING', {subject: 'structureDelete', loading: false});
            });
    },

};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
